/**
 * Maps educator dashboard `p_application` rows into the shape expected by the same
 * queue rules used by {@link QueueStatusFilterDirective} (admin / intake / etc.).
 */

export type QueueStatusRule = {
  appStatus: string;
  bgStatus: string[];
  applicationStatus: string;
};

/**
 * Build a row with `application_status` (status code), `background_check_status`,
 * `application_type`, and `bg_required` for queue rule resolution.
 */
export function buildQueueStatusRowFromEducatorApplication(
  app: Record<string, unknown>,
  fingerprintFallback: string
): Record<string, unknown> {
  const code = String(app['cd_application_status'] ?? app['application_status_code'] ?? '').trim();
  const bgRaw = String(app['background_check_status'] ?? '').trim();
  const bg = bgRaw || String(fingerprintFallback ?? '').trim() || '-';
  const application_type = String(app['app_type'] ?? app['application_type'] ?? '').trim();
  return {
    ...app,
    application_status: code || String(app['application_status'] ?? '').trim(),
    background_check_status: bg,
    application_type,
    bg_required: app['bg_required'] === true
  };
}

/**
 * Resolve display label from queue rules (same matching logic as QueueStatusFilterDirective).
 * Returns `null` when no rule matches so callers can fall back (e.g. combinedApplicationStatus).
 */
export function resolveQueueApplicationStatusDisplay(
  item: Record<string, unknown>,
  rules: QueueStatusRule[],
  queueName: string
): string | null {
  if (!rules?.length) return null;
  const cd_application_status =
    item['application_status'] != null ? String(item['application_status']) : '';
  const background_check_status = item['background_check_status']
    ? String(item['background_check_status'])
    : '-';
  const application_type = item['application_type'] ? String(item['application_type']) : '';
  const qn = String(queueName ?? '').toLowerCase();

  for (const rule of rules) {
    if (rule.appStatus !== cd_application_status) continue;

    if (application_type === 'Surrender') {
      if (qn === 'analyst' || qn === 'admin' || qn === 'dashboardadmin') {
        if (!rule.bgStatus || rule.bgStatus.length === 0) {
          return rule.applicationStatus;
        }
      }
      continue;
    }

    if (item['bg_required'] === true) {
      if (rule.bgStatus?.includes('-')) {
        return rule.applicationStatus;
      }
      if (rule.bgStatus?.includes(background_check_status)) {
        return rule.applicationStatus;
      }
      if (qn === 'dashboardadmin' && (!rule.bgStatus || rule.bgStatus.length === 0)) {
        return rule.applicationStatus;
      }
    } else {
      if (
        cd_application_status === '17' &&
        (qn === 'intake' || qn === 'admin' || qn === 'dashboardadmin')
      ) {
        return rule.applicationStatus;
      }
      if (!rule.bgStatus || rule.bgStatus.length === 0) {
        return rule.applicationStatus;
      }
    }
  }

  return null;
}
